import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Mail, Phone, Globe, MapPin, Linkedin, Twitter, Github, 
  Instagram, Facebook, Youtube, Briefcase, Building, 
  MessageCircle, Download, ExternalLink, ChevronLeft, ChevronRight,
  Sparkles, Image, Play, X, Share2, Copy, Check, Menu, Home,
  User, Users, ImageIcon, Video
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { generateAndDownloadVCard } from '../utils/vcardGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Web = () => {
  const { username } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Section refs for scroll detection
  const sectionRefs = {
    home: useRef(null),
    about: useRef(null),
    contact: useRef(null),
    social: useRef(null),
    gallery: useRef(null),
    videos: useRef(null),
  };

  useEffect(() => {
    const fetchCard = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/cards/${username}`);
        if (!response.ok) {
          throw new Error('Card not found');
        }
        const data = await response.json();
        setCard(data);
      } catch (err) {
        console.error('Error fetching card:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (username) {
      fetchCard();
    }
  }, [username]);

  // Update favicon dynamically when card loads
  useEffect(() => {
    const userImage = card?.profileImage || card?.avatar;
    if (userImage) {
      const updateFavicon = (selector, href) => {
        let link = document.querySelector(selector);
        if (link) {
          link.href = href;
        } else {
          link = document.createElement('link');
          link.rel = selector.includes('apple') ? 'apple-touch-icon' : 'icon';
          if (selector.includes('32x32')) {
            link.sizes = '32x32';
          }
          link.type = 'image/png';
          link.href = href;
          document.head.appendChild(link);
        }
      };
      
      updateFavicon('link[rel="icon"]:not([sizes])', userImage);
      updateFavicon('link[rel="icon"][sizes="32x32"]', userImage);
      updateFavicon('link[rel="apple-touch-icon"]', userImage);
    }
    
    return () => {
      const restoreFavicon = (selector, defaultHref) => {
        const link = document.querySelector(selector);
        if (link) {
          link.href = defaultHref;
        }
      };
      
      restoreFavicon('link[rel="icon"]:not([sizes])', '/logo.png');
      restoreFavicon('link[rel="icon"][sizes="32x32"]', '/logo.png');
      restoreFavicon('link[rel="apple-touch-icon"]', '/logo.png');
    };
  }, [card?.profileImage, card?.avatar]);

  // Intersection Observer for active section detection
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, [card]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navHeight,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleSaveContact = () => {
    if (card) {
      generateAndDownloadVCard(card);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: card?.name, url });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getWebsiteUrl = () => {
    if (card?.website) return card.website;
    if (card?.websiteName && card?.websiteTLD) {
      return `https://${card.websiteName}${card.websiteTLD}`;
    }
    return null;
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxImage(card.galleryImages[index]);
  };

  const nextImage = () => {
    const newIndex = (lightboxIndex + 1) % card.galleryImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(card.galleryImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (lightboxIndex - 1 + card.galleryImages.length) % card.galleryImages.length;
    setLightboxIndex(newIndex);
    setLightboxImage(card.galleryImages[newIndex]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-amber-500/30 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-3">Page Not Found</h1>
          <p className="text-slate-400 mb-6">This mini website doesn't exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium transition-colors">
            <ChevronLeft className="w-4 h-4" />
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  const websiteUrl = getWebsiteUrl();
  const hasGallery = card.galleryImages && card.galleryImages.length > 0;
  const hasVideos = card.youtubeVideos && card.youtubeVideos.length > 0;
  const hasSocials = card.linkedin || card.twitter || card.instagram || card.facebook || card.youtube || card.github;
  const hasAbout = card.bio;

  // Build navigation items based on available content
  const navItems = [
    { id: 'home', label: 'Home', icon: Home, show: true },
    { id: 'about', label: 'About', icon: User, show: hasAbout },
    { id: 'contact', label: 'Contact', icon: Phone, show: true },
    { id: 'social', label: 'Social', icon: Users, show: hasSocials },
    { id: 'gallery', label: 'Gallery', icon: ImageIcon, show: hasGallery },
    { id: 'videos', label: 'Videos', icon: Video, show: hasVideos },
  ].filter(item => item.show);

  return (
    <>
      <Helmet>
        <title>{card.name} | Portfolio</title>
        <meta name="description" content={card.bio || `${card.name}'s professional portfolio`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        
        {/* Fixed Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-3">
          <div className="max-w-6xl mx-auto">
            <div className="bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-2 flex items-center justify-between">
              {/* Logo/Profile */}
              <button 
                onClick={() => scrollToSection('home')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              >
                {card.profileImage ? (
                  <img 
                    src={card.profileImage} 
                    alt={card.name}
                    className="w-9 h-9 rounded-xl object-cover ring-2 ring-amber-400/50"
                  />
                ) : (
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                    {card.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <span className="font-semibold text-white hidden sm:block text-sm">{card.name?.split(' ')[0]}</span>
              </button>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      activeSection === item.id
                        ? 'bg-amber-500 text-white'
                        : 'text-slate-300 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleShare}
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/10 hidden sm:flex"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                </Button>
                <Button 
                  onClick={handleSaveContact}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white text-xs"
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline ml-1">Save</span>
                </Button>
                
                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="md:hidden p-2 text-white hover:bg-white/10 rounded-xl transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {mobileMenuOpen && (
              <div className="md:hidden mt-2 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-2 animate-in slide-in-from-top-2">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                        activeSection === item.id
                          ? 'bg-amber-500 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </button>
                  );
                })}
                <hr className="my-2 border-white/10" />
                <button
                  onClick={handleShare}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10"
                >
                  <Share2 className="w-5 h-5" />
                  {copied ? 'Link Copied!' : 'Share Profile'}
                </button>
              </div>
            )}
          </div>
        </nav>

        {/* HOME Section */}
        <section 
          id="home" 
          ref={sectionRefs.home}
          className="min-h-screen flex items-center justify-center pt-24 pb-16 px-4 relative overflow-hidden"
        >
          {/* Decorative Elements */}
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-[128px] pointer-events-none"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[128px] pointer-events-none"></div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Profile Image */}
            <div className="mb-8">
              {card.profileImage ? (
                <img 
                  src={card.profileImage} 
                  alt={card.name}
                  className="w-36 h-36 sm:w-44 sm:h-44 rounded-3xl object-cover mx-auto shadow-2xl ring-4 ring-white/10"
                />
              ) : (
                <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-6xl font-bold mx-auto shadow-2xl">
                  {card.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>

            {/* Name & Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight">
              {card.name}
            </h1>
            
            {card.title && (
              <p className="text-xl sm:text-2xl text-amber-400 font-medium mb-3 flex items-center justify-center gap-2">
                <Briefcase className="w-5 h-5 sm:w-6 sm:h-6" />
                {card.title}
              </p>
            )}
            
            {card.company && (
              <p className="text-lg text-slate-300 flex items-center justify-center gap-2 mb-2">
                <Building className="w-5 h-5" />
                {card.company}
              </p>
            )}
            
            {card.location && (
              <p className="text-slate-400 flex items-center justify-center gap-2">
                <MapPin className="w-4 h-4" />
                {card.location}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mt-10 justify-center">
              {card.phone && (
                <a 
                  href={`tel:${card.phone}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all hover:scale-105 shadow-lg shadow-green-500/25 text-sm"
                >
                  <Phone className="w-4 h-4" />
                  Call
                </a>
              )}
              {card.email && (
                <a 
                  href={`mailto:${card.email}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-all hover:scale-105 shadow-lg shadow-blue-500/25 text-sm"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
              )}
              {card.whatsapp && (
                <a 
                  href={`https://wa.me/${card.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-all hover:scale-105 shadow-lg shadow-emerald-500/25 text-sm"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              )}
              {websiteUrl && (
                <a 
                  href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-medium transition-all hover:scale-105 shadow-lg shadow-purple-500/25 text-sm"
                >
                  <Globe className="w-4 h-4" />
                  Website
                </a>
              )}
            </div>

            {/* Scroll Indicator */}
            <button 
              onClick={() => scrollToSection(hasAbout ? 'about' : 'contact')}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer"
            >
              <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
                <div className="w-1.5 h-3 bg-white/50 rounded-full animate-pulse"></div>
              </div>
            </button>
          </div>
        </section>

        {/* ABOUT Section */}
        {hasAbout && (
          <section 
            id="about" 
            ref={sectionRefs.about}
            className="py-20 px-4"
          >
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-amber-500/20 rounded-2xl flex items-center justify-center">
                  <User className="w-6 h-6 text-amber-400" />
                </div>
                <h2 className="text-3xl font-bold text-white">About Me</h2>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 sm:p-10">
                <div 
                  className="text-slate-300 text-lg leading-relaxed prose prose-invert prose-lg max-w-none
                    prose-strong:text-white prose-a:text-amber-400 prose-a:no-underline hover:prose-a:underline"
                  dangerouslySetInnerHTML={{ __html: card.bio }}
                />
              </div>
            </div>
          </section>
        )}

        {/* CONTACT Section */}
        <section 
          id="contact" 
          ref={sectionRefs.contact}
          className="py-20 px-4 bg-black/20"
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                <Phone className="w-6 h-6 text-blue-400" />
              </div>
              <h2 className="text-3xl font-bold text-white">Contact</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {card.email && (
                <a 
                  href={`mailto:${card.email}`}
                  className="group flex items-center gap-4 p-5 bg-white/5 hover:bg-blue-500/20 border border-white/10 hover:border-blue-500/50 rounded-2xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-blue-500/20 group-hover:bg-blue-500 rounded-xl flex items-center justify-center transition-colors">
                    <Mail className="w-7 h-7 text-blue-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-400 mb-1">Email</p>
                    <p className="text-white font-medium truncate">{card.email}</p>
                  </div>
                </a>
              )}

              {card.phone && (
                <a 
                  href={`tel:${card.phone}`}
                  className="group flex items-center gap-4 p-5 bg-white/5 hover:bg-green-500/20 border border-white/10 hover:border-green-500/50 rounded-2xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-green-500/20 group-hover:bg-green-500 rounded-xl flex items-center justify-center transition-colors">
                    <Phone className="w-7 h-7 text-green-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-400 mb-1">Phone</p>
                    <p className="text-white font-medium truncate">{card.phone}</p>
                  </div>
                </a>
              )}

              {websiteUrl && (
                <a 
                  href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-5 bg-white/5 hover:bg-purple-500/20 border border-white/10 hover:border-purple-500/50 rounded-2xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-purple-500/20 group-hover:bg-purple-500 rounded-xl flex items-center justify-center transition-colors">
                    <Globe className="w-7 h-7 text-purple-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-400 mb-1">Website</p>
                    <p className="text-white font-medium truncate">{websiteUrl.replace(/^https?:\/\//, '')}</p>
                  </div>
                </a>
              )}

              {card.location && (
                <a 
                  href={`https://maps.google.com/?q=${encodeURIComponent(card.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-5 bg-white/5 hover:bg-orange-500/20 border border-white/10 hover:border-orange-500/50 rounded-2xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-orange-500/20 group-hover:bg-orange-500 rounded-xl flex items-center justify-center transition-colors">
                    <MapPin className="w-7 h-7 text-orange-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-400 mb-1">Location</p>
                    <p className="text-white font-medium truncate">{card.location}</p>
                  </div>
                </a>
              )}

              {card.whatsapp && (
                <a 
                  href={`https://wa.me/${card.whatsapp.replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-5 bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/50 rounded-2xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-emerald-500/20 group-hover:bg-emerald-500 rounded-xl flex items-center justify-center transition-colors">
                    <MessageCircle className="w-7 h-7 text-emerald-400 group-hover:text-white transition-colors" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-400 mb-1">WhatsApp</p>
                    <p className="text-white font-medium truncate">{card.whatsapp}</p>
                  </div>
                </a>
              )}
            </div>
          </div>
        </section>

        {/* SOCIAL Section */}
        {hasSocials && (
          <section 
            id="social" 
            ref={sectionRefs.social}
            className="py-20 px-4"
          >
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-pink-500/20 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-pink-400" />
                </div>
                <h2 className="text-3xl font-bold text-white">Social</h2>
              </div>
              <div className="flex flex-wrap gap-4">
                {card.linkedin && (
                  <a 
                    href={card.linkedin.startsWith('http') ? card.linkedin : `https://linkedin.com/in/${card.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-[#0077b5] hover:bg-[#006399] text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-[#0077b5]/25"
                  >
                    <Linkedin className="w-6 h-6" />
                    <span className="font-medium">LinkedIn</span>
                  </a>
                )}
                {card.twitter && (
                  <a 
                    href={card.twitter.startsWith('http') ? card.twitter : `https://twitter.com/${card.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-[#1da1f2] hover:bg-[#0d8ecf] text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-[#1da1f2]/25"
                  >
                    <Twitter className="w-6 h-6" />
                    <span className="font-medium">Twitter</span>
                  </a>
                )}
                {card.instagram && (
                  <a 
                    href={card.instagram.startsWith('http') ? card.instagram : `https://instagram.com/${card.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-[#f09433] via-[#e6683c] to-[#dc2743] hover:opacity-90 text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-[#e6683c]/25"
                  >
                    <Instagram className="w-6 h-6" />
                    <span className="font-medium">Instagram</span>
                  </a>
                )}
                {card.facebook && (
                  <a 
                    href={card.facebook.startsWith('http') ? card.facebook : `https://facebook.com/${card.facebook}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-[#1877f2] hover:bg-[#0d65d9] text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-[#1877f2]/25"
                  >
                    <Facebook className="w-6 h-6" />
                    <span className="font-medium">Facebook</span>
                  </a>
                )}
                {card.youtube && (
                  <a 
                    href={card.youtube.startsWith('http') ? card.youtube : `https://youtube.com/${card.youtube}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-[#ff0000] hover:bg-[#cc0000] text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-[#ff0000]/25"
                  >
                    <Youtube className="w-6 h-6" />
                    <span className="font-medium">YouTube</span>
                  </a>
                )}
                {card.github && (
                  <a 
                    href={card.github.startsWith('http') ? card.github : `https://github.com/${card.github}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-6 py-4 bg-[#333] hover:bg-[#222] text-white rounded-xl transition-all hover:scale-105 shadow-lg shadow-black/25"
                  >
                    <Github className="w-6 h-6" />
                    <span className="font-medium">GitHub</span>
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        {/* GALLERY Section */}
        {hasGallery && (
          <section 
            id="gallery" 
            ref={sectionRefs.gallery}
            className="py-20 px-4 bg-black/20"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-violet-500/20 rounded-2xl flex items-center justify-center">
                  <ImageIcon className="w-6 h-6 text-violet-400" />
                </div>
                <h2 className="text-3xl font-bold text-white">Gallery</h2>
                <span className="text-sm text-slate-400 bg-white/10 px-3 py-1 rounded-full">
                  {card.galleryImages.length} {card.galleryImages.length === 1 ? 'photo' : 'photos'}
                </span>
              </div>
              
              <div className={`grid gap-4 ${
                card.galleryImages.length === 1 ? 'grid-cols-1 max-w-3xl' :
                card.galleryImages.length === 2 ? 'grid-cols-2' :
                'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
              }`}>
                {card.galleryImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => openLightbox(index)}
                    className={`group relative rounded-2xl overflow-hidden bg-slate-800 hover:shadow-2xl hover:shadow-amber-500/10 transition-all duration-500 ${
                      card.galleryImages.length === 1 ? 'aspect-video' :
                      card.galleryImages.length === 2 ? 'aspect-[4/3]' :
                      index === 0 && card.galleryImages.length > 2 ? 'sm:col-span-2 aspect-video' : 'aspect-square'
                    }`}
                  >
                    <img 
                      src={image} 
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                        <span className="text-white font-medium text-sm">View Full Size</span>
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                          <ExternalLink className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* VIDEOS Section */}
        {hasVideos && (
          <section 
            id="videos" 
            ref={sectionRefs.videos}
            className="py-20 px-4"
          >
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-red-500/20 rounded-2xl flex items-center justify-center">
                  <Play className="w-6 h-6 text-red-400" />
                </div>
                <h2 className="text-3xl font-bold text-white">Videos</h2>
                <span className="text-sm text-slate-400 bg-white/10 px-3 py-1 rounded-full">
                  {card.youtubeVideos.length} {card.youtubeVideos.length === 1 ? 'video' : 'videos'}
                </span>
              </div>
              
              <div className={`grid gap-6 ${
                card.youtubeVideos.length === 1 ? 'grid-cols-1 max-w-4xl' :
                'grid-cols-1 lg:grid-cols-2'
              }`}>
                {card.youtubeVideos.map((videoId, index) => (
                  <div 
                    key={index} 
                    className="rounded-2xl overflow-hidden shadow-2xl shadow-black/50 bg-slate-900 group hover:ring-2 hover:ring-red-500/50 transition-all"
                  >
                    <div className="aspect-video">
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?rel=0`}
                        title={`Video ${index + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="py-10 px-4 border-t border-white/10">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-slate-500 text-sm">
                © {new Date().getFullYear()} {card.name}
              </p>
              <Link 
                to="/" 
                className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-colors text-sm"
              >
                <span>Powered by</span>
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="font-semibold text-white">Piper Card</span>
              </Link>
            </div>
          </div>
        </footer>

        {/* Image Lightbox */}
        {lightboxImage && (
          <div 
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setLightboxImage(null)}
          >
            <button 
              onClick={() => setLightboxImage(null)}
              className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            {card.galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 sm:left-8 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 sm:right-8 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}
            
            <img 
              src={lightboxImage} 
              alt="Gallery" 
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            {card.galleryImages.length > 1 && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm">
                {lightboxIndex + 1} / {card.galleryImages.length}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Web;
